import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import Cropper, { ReactCropperElement } from 'react-cropper';

interface ImageCropperProps {
  imageSrc: string;
  onCropComplete: (croppedImageBlob: Blob) => void;
  onCancel: () => void;
}

const CropperContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.95);
  z-index: 9999;
  display: flex;
  flex-direction: column;
`;

const CropperHeader = styled.div`
  padding: 20px;
  background-color: rgba(0, 0, 0, 0.9);
  color: white;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  border-bottom: 2px solid white;
`;

const CropperArea = styled.div`
  flex: 1;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  .cropper-view-box,
  .cropper-face {
    border-radius: 0;
  }
  
  .cropper-view-box {
    outline: 2px solid white;
    outline-offset: -1px;
  }
  
  .cropper-point {
    background-color: white;
    width: 12px;
    height: 12px;
  }
  
  .cropper-line {
    background-color: white;
  }
  
  .cropper-dashed {
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const CropperControls = styled.div`
  padding: 20px;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;
  gap: 15px;
  border-top: 2px solid white;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
`;

const Button = styled.button<{ $primary?: boolean }>`
  padding: 12px 30px;
  font-size: 16px;
  font-weight: 600;
  border: 2px solid white;
  border-radius: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: var(--color-black);
  color: white;

  &:hover {
    background-color: white;
    color: var(--color-black);
    border-color: white;
  }

  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HelpText = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  text-align: center;
`;

const ImageCropper: React.FC<ImageCropperProps> = ({
  imageSrc,
  onCropComplete,
  onCancel
}) => {
  const cropperRef = useRef<ReactCropperElement>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleCrop = async () => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) {
      return;
    }

    setIsProcessing(true);

    try {
      // 获取裁切后的 canvas
      const canvas = cropper.getCroppedCanvas();
      
      if (!canvas) {
        throw new Error('Failed to get cropped canvas');
      }

      // 转换为 Blob
      canvas.toBlob((blob) => {
        if (blob) {
          onCropComplete(blob);
        } else {
          alert('Failed to crop image. Please try again.');
        }
        setIsProcessing(false);
      }, 'image/png', 1);
    } catch (error) {
      console.error('Error cropping image:', error);
      alert('Failed to crop image. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <CropperContainer>
      <CropperHeader>
        Crop Pattern Area
      </CropperHeader>
      
      <CropperArea>
        <Cropper
          ref={cropperRef}
          src={imageSrc}
          style={{ 
            height: '100%', 
            width: '100%',
            maxHeight: '70vh'
          }}
          // Cropper.js options
          viewMode={1}
          dragMode="move"
          aspectRatio={NaN} // 自由裁切，无固定长宽比
          autoCropArea={0.8}
          restore={false}
          guides={true}
          center={true}
          highlight={true}
          cropBoxMovable={true}
          cropBoxResizable={true}
          toggleDragModeOnDblclick={false}
          background={false}
          responsive={true}
          checkOrientation={false}
        />
      </CropperArea>

      <CropperControls>
        <HelpText>
          Drag corners and edges to adjust crop area. Drag inside to move. Free-form rectangular cropping.
        </HelpText>

        <ButtonContainer>
          <Button onClick={onCancel} disabled={isProcessing}>
            Cancel
          </Button>
          <Button $primary onClick={handleCrop} disabled={isProcessing}>
            {isProcessing ? 'Processing...' : 'Confirm'}
          </Button>
        </ButtonContainer>
      </CropperControls>
    </CropperContainer>
  );
};

export default ImageCropper;
